/**
 * Debounce delays for user input and DataGrid quick filters (milliseconds).
 */
export const DEBOUNCE_MS = {
  /** Disable built-in debounce when debouncing elsewhere (e.g. server-backed quick filter hook). */
  NONE: 0,
  /** Fast preview fields or grid autosize defer. */
  FAST: 100,
  /** Default for useDebouncedValue, useDebouncedSearch, useServerBackedQuickFilter. */
  DEFAULT: 300,
  /** Server-backed advisor client grid search. */
  SERVER_QUICK_FILTER: 350,
  /** Remote autocomplete fetch after typing stops. */
  AUTOCOMPLETE: 250,
  /** SearchBar onSearch and dashboard client-side filter inputs. */
  SEARCH: 500,
  /** Large client-side DataGrid quick filters (e.g. cash flow rules). */
  QUICK_FILTER_HEAVY: 500,
} as const;

/** Non-debounce delays for layout, export, and lifecycle (milliseconds). */
export const TIMING_MS = {
  /** Wait for grid render before autosizeColumns. */
  GRID_AUTOSIZE_DELAY: 100,
  /** Deferred grid layout re-measure (e.g. XS grouping). */
  GRID_LAYOUT_DEFER: 50,
  /** Delay before revoking blob URLs after open/download. */
  BLOB_REVOKE_DELAY: 100,
  /** Defer chart/export until DOM is ready. */
  EXPORT_RENDER_DEFER: 100,
  /** Delay before print after layout class applied. */
  PRINT_LAYOUT_DELAY: 500,
  /** Delay between chained cash-flow wizards. */
  WIZARD_CHAIN_DELAY: 300,
} as const;

/** CSS/MUI transition durations (milliseconds). */
export const TRANSITION_MS = {
  /** Standard opacity/width transitions and flyout duration constant. */
  DEFAULT: 300,
  /** Duplicate transaction wizard step fade. */
  WIZARD_STEP: 180,
} as const;

/** CSS properties supported by {@link transitionCss} call sites. */
export type TransitionCssProperty = 'opacity' | 'width';

/** CSS transition timing functions supported by {@link transitionCss}. */
export type TransitionCssTiming =
  | 'ease'
  | 'ease-in'
  | 'ease-out'
  | 'ease-in-out'
  | 'linear';

export interface TransitionCssOptions {
  durationMs: number;
  property: TransitionCssProperty;
  timing?: TransitionCssTiming;
}

/** Build a CSS transition value, e.g. `opacity 300ms ease-in-out`. */
export function transitionCss({
  durationMs,
  property,
  timing = 'ease-in-out',
}: TransitionCssOptions): string {
  return `${property} ${durationMs}ms ${timing}`;
}
